/**
 * The two writes on Performance (WALLIYO-0131): opening a cycle, and writing
 * a review.
 *
 * Writing has two buttons because it has two meanings. Saving keeps it to
 * yourself and may be as empty as you like; submitting is the moment it
 * becomes somebody's answer, and the server refuses one that says nothing.
 */

import type { PerformanceReview } from "@wallyo/contracts/console/hr";
import { useState } from "react";
import { openReviewCycle, writeReview } from "../../api/console-api";
import { useApi } from "../../api/context";
import { t } from "../../i18n";
import { ReasonDialogView, useIntentWrite } from "../team/TeamDialogs";

type Common = { onClose: () => void; onReload: () => void; onDone: (message: string) => void };

export function OpenCycleDialog(props: Common) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [name, setName] = useState("");
  const [opensOn, setOpensOn] = useState("");
  const [closesOn, setClosesOn] = useState("");
  const [reason, setReason] = useState("");

  const problem =
    name.trim().length < 2
      ? t("performance.form.name")
      : opensOn === "" || closesOn === ""
        ? t("performance.form.opens")
        : closesOn < opensOn
          ? t("performance.form.closes")
          : null;

  return (
    <ReasonDialogView
      title={t("performance.form.title")}
      body={<p className="meta">{t("performance.toWrite.help")}</p>}
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("performance.form.submit")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          problem,
          (key) =>
            openReviewCycle(
              client,
              { name: name.trim(), opensOn, closesOn, reason: reason.trim() },
              { idempotencyKey: key },
            ),
          ({ cycle, reviews }) =>
            props.onDone(t("performance.opened", { name: cycle.name, count: String(reviews.length) })),
        )
      }
    >
      <div className="field">
        <label htmlFor="cycle-name">{t("performance.form.name")}</label>
        <input
          id="cycle-name"
          type="text"
          value={name}
          onChange={(event) => {
            write.edited();
            setName(event.currentTarget.value);
          }}
        />
      </div>
      <div className="field">
        <label htmlFor="cycle-opens">{t("performance.form.opens")}</label>
        <input
          id="cycle-opens"
          type="date"
          value={opensOn}
          onChange={(event) => {
            write.edited();
            setOpensOn(event.currentTarget.value);
          }}
        />
      </div>
      <div className="field">
        <label htmlFor="cycle-closes">{t("performance.form.closes")}</label>
        <input
          id="cycle-closes"
          type="date"
          value={closesOn}
          onChange={(event) => {
            write.edited();
            setClosesOn(event.currentTarget.value);
          }}
        />
      </div>
    </ReasonDialogView>
  );
}

export function WriteReviewDialog(props: Common & { review: PerformanceReview }) {
  const { client } = useApi();
  const write = useIntentWrite();
  const { review } = props;
  const [outcome, setOutcome] = useState(review.outcome ?? "");
  const [note, setNote] = useState(review.note ?? "");

  // The reason field carries the note, because the note is what the audit
  // entry should say about this write. One thing typed once.
  const send = (submit: boolean) =>
    void write.run(
      submit && outcome.trim().length === 0 ? t("performance.writeForm.outcome") : null,
      (key) =>
        writeReview(
          client,
          review.performanceReviewId,
          { outcome: outcome.trim(), note: note.trim(), submit, version: review.version },
          { idempotencyKey: key },
        ),
      ({ review: written }) =>
        props.onDone(
          written.status === "submitted" ? t("performance.sent", { name: written.name }) : t("performance.saved"),
        ),
    );

  return (
    <ReasonDialogView
      title={t("performance.writeForm.title")}
      body={
        <p className="meta">
          {review.name}
          <br />
          {t("performance.writeForm.outcomeHelp")}
        </p>
      }
      reason={note}
      onReason={(next) => {
        write.edited();
        setNote(next);
      }}
      submitLabel={t("performance.writeForm.send")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() => send(true)}
    >
      <div className="field">
        <label htmlFor="review-outcome">{t("performance.writeForm.outcome")}</label>
        <input
          id="review-outcome"
          type="text"
          value={outcome}
          onChange={(event) => {
            write.edited();
            setOutcome(event.currentTarget.value);
          }}
        />
      </div>
      <div className="btns">
        <button className="btn quiet" type="button" disabled={write.pending} onClick={() => send(false)}>
          {t("performance.writeForm.save")}
        </button>
      </div>
    </ReasonDialogView>
  );
}
